const { GAME_ACTION, GAME_EVENT, UNIT_TYPE, CARD_RARITY } = require('../const/')

const card = {
    create,
    validate,
    options,
    bot,
    rule,
    type: 'PROJECTILEBOUNCE',
    title: 'Projectile Bounce',
    description:
        "The Slinger's projectile bounces off all surrounding units, dealing one damage to each.",
    power: {
        [CARD_RARITY.COMMON]: 15,
        [CARD_RARITY.UNCOMMON]: 14,
        [CARD_RARITY.RARE]: 13,
        [CARD_RARITY.EPIC]: 12,
        [CARD_RARITY.LEGENDARY]: 11,
    },
}

module.exports = card

function create({ emitter, board, player_id }) {
    const state = {}

    // PUBLIC
    // PUBLIC
    // PUBLIC
    function init() {
        emitter.emit(GAME_EVENT.MESSAGE_SET, {
            message: 'Select one Slinger',
        })
        emitter.on(GAME_EVENT.TOUCH_CLICK, click)
        update()
    }

    function update() {
        clear()
        const tiles = options({ board, player_id }).map(
            ({ unit_id }) => board.state.units[unit_id].tile_id
        )
        state.tiles = tiles
        emitter.emit(GAME_EVENT.TILES_HIGHLIGHT_SHOW, {
            tiles: tiles.map((tile_id) => ({ tile_id })),
        })
        // emitter.emit(GAME_EVENT.CAMERA_POSITION_TILES, { tiles })
    }

    function destroy() {
        clear()
        emitter.emit(GAME_EVENT.MESSAGE_UNSET, {})
        emitter.remove(GAME_EVENT.TOUCH_CLICK, click)
    }

    // PRIVATE
    // PRIVATE
    // PRIVATE
    function clear() {
        if (state.tiles !== undefined) {
            emitter.emit(GAME_EVENT.TILES_HIGHLIGHT_HIDE, {
                tiles: state.tiles,
            })
        }
    }

    function click({ position }) {
        const tile_id = board.positionToTile(position)
        const params = { tile_id }
        const valid = validate({ board, player_id, params })
        if (valid !== undefined) {
            emitter.emit(GAME_EVENT.CARD_USING, params)
        }
    }

    return {
        state,
        init,
        update,
        destroy,
    }
}

function options({ board, player_id }) {
    const team_id = board.getTeamByPlayer({ player_id })
    const already = Object.keys(board.state.temp)
        .filter((uid) => {
            const temp = board.state.temp[uid]
            return (
                temp.card_type === card.type &&
                board.state.units.hasOwnProperty(temp.subparams.unit_id)
            )
        })
        .map((uid) => board.state.temp[uid].subparams.unit_id)

    return board
        .getUnitsByTeam({ team_id })
        .filter((unit_id) =>
            [UNIT_TYPE.SLINGER].includes(board.state.units[unit_id].unit_type)
        )
        .filter((unit_id) => !already.includes(unit_id))
        .map((unit_id) => ({ unit_id }))
}

function validate({ board, player_id, params }) {
    const valid = options({ board, player_id })
        .map(({ unit_id }) => board.state.units[unit_id].tile_id)
        .includes(params.tile_id)

    if (valid) {
        return { unit_id: board.getUnitByTile({ tile_id: params.tile_id }) }
    }
}

function bot({ board, action, reactions }) {
    if (action.type === GAME_ACTION.UNIT_ATTACK) {
        const bounces = getBounces({ board, params: action.params, reactions })
        if (bounces.length > 0) {
            return 1
        }
    }
    return 0
}

function rule({ action, board, reactions, addReaction, dop }) {
    const { type, params, uid } = action
    const icon = `card_${card.type.toLowerCase()}`

    // ACTIVE
    if (type === GAME_ACTION.CARD_USE && params.card_type === card.type) {
        const { unit_id } = params.subparams
        addReaction({
            type: GAME_ACTION.CARD_REACTION_ADD,
            params: {
                uid,
                card_type: card.type,
                subparams: params.subparams,
                reactions: [
                    {
                        type: GAME_ACTION.UNIT_ICON_ADD,
                        params: {
                            uid,
                            icon,
                            unit_id,
                        },
                    },
                ],
            },
        })
    }

    // ATTACK
    else if (type === GAME_ACTION.UNIT_ATTACK) {
        Object.keys(board.state.temp).forEach((uid) => {
            const temp = board.state.temp[uid]
            if (
                temp !== undefined &&
                temp.card_type === card.type &&
                temp.subparams.unit_id === params.unit_id
            ) {
                const bounces = getBounces({ board, params, reactions })

                bounces.forEach((unit_id_bounce) => {
                    const unit2 = board.state.units[unit_id_bounce]
                    const damage = 1
                    const newlife = unit2.life - damage
                    addReaction({
                        type: GAME_ACTION.UNIT_HIT,
                        params: {
                            unit_id: unit_id_bounce,
                            unit_id_origin: params.unit_id,
                            life: newlife,
                            damage: damage,
                        },
                    })
                })

                addReaction({
                    type: GAME_ACTION.CARD_REACTION_REMOVE,
                    params: {
                        uid,
                        reactions: [
                            {
                                type: GAME_ACTION.UNIT_ICON_REMOVE,
                                params: {
                                    uid,
                                    unit_id: params.unit_id,
                                },
                            },
                        ],
                    },
                })
            }
        })
    }
}

function getBounces({ board, params, reactions }) {
    const unit_target = board.state.units[params.unit_id_target]
    let tile_id

    // If unit_target is death we must find the UNIT_REMOVE reaction to find the tile_id of the unit_target
    if (unit_target === undefined) {
        const unit_removed = reactions.find(
            (r) =>
                r.type === GAME_ACTION.UNIT_REMOVE &&
                r.params.unit_id === params.unit_id_target
        )
        tile_id = unit_removed.unpatch.units[params.unit_id_target].tile_id
    } else {
        tile_id = unit_target.tile_id
    }

    const square = board.getRangeFromTile({
        tile_id,
        range: 1,
    })
    const tiles = board.getTilesFromSquare(square)
    const enemies = board.getEnemies({ unit_id: params.unit_id })
    const bounces = tiles
        .map((tile_id) => board.getUnitByTile({ tile_id }))
        .filter((unit_id) => unit_id !== undefined)
        .filter((unit_id) => unit_id !== params.unit_id)
        .filter((unit_id) => unit_id !== params.unit_id_target)
        .filter((unit_id) => enemies.includes(unit_id))

    return bounces
}
